import React, { useEffect, useState, useRef, useMemo } from "react";
import { TextField } from "../common/inputFiled";
import { PhoneField } from "../common/phoneFiled";
import OtpModal from "../common/OtpModal";
import Cookies from "js-cookie";
import { updateAuthTokenFromResponse } from "../../utils/authSession";
import toast from "react-hot-toast";
import { CountryModel } from "../common/countryModel";
import { LocationModal } from "./locationModel";
// Mappls picker kept for future rollback — switch import below to re-enable.
// import { MapplsMapPicker } from "./mapplsMapPicker";
import { GoogleMapPicker } from "./googleMapPicker";
import { BirthCityCountryModal } from "./birthCityCountryModal";
import usePaymentStatus from "../../hooks/usePaymentStatus";

// Module-level constants — defined once, never recreated per render/call
const CAPITALIZE_FIELDS = new Set([]);

const PERSONAL_KEYS = [
  "first_name", "last_name", "full_name", "personal_email_id", "work_email_id", "secondary_email",
  "marital_status", "date_of_birth", "date_of_anniversary", "birth_city_country", "current_city_region_country",
  "country_code_personal", "personal_mobile",
  "basic_pincode_home",
  "country_code_home", "secondary_mobile",
  "work_mobile_country_code", "work_mobile",
  "country_code_home_fixed", "home_fixed", "country_code_work_fixed", "work_fixed",
];

const ADDRESS_KEYS = [
  "flat_number", "building_name", "street_name", "city_region_country", "home_address_pincode",
  "address_google_map", "home_address_latitude", "home_address_longitude", "area_locality",
  "home_address_city", "home_address_region", "home_address_country",
];

const MARITAL_STATUS_OPTIONS = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Divorced", label: "Divorced" },
  { value: "Widowed", label: "Widowed" },
  { value: "Separated", label: "Separated" },
];

const VALID_MARITAL_STATUS = new Set(MARITAL_STATUS_OPTIONS.map((o) => o.value));

const INLINE_CONTACT_FIELDS = {
  personal_mobile: {
    label: "Personal Mobile",
    endpointBase: "/personal-mobile",
    bodyKey: "personal_mobile",
    countryKey: "personal_mobile_country_code",
  },
  personal_email_id: {
    label: "Personal Email Id",
    endpointBase: "/personal-email",
    bodyKey: "personal_email_id",
    countryKey: null,
  },
  work_email_id: {
    label: "Work Email Id",
    endpointBase: "/work-email",
    bodyKey: "work_email_id",
    countryKey: null,
  },
  work_mobile: {
    label: "Work Mobile",
    endpointBase: "/work-mobile",
    bodyKey: "work_mobile",
    countryKey: "work_mobile_country_code",
  },
  secondary_mobile: {
    label: "Secondary Mobile",
    endpointBase: "/secondary-mobile",
    bodyKey: "secondary_mobile",
    countryKey: "secondary_mobile_country_code",
  },
};

const isEmail = (val) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(val || "").trim());

const isUrlAddress = (value) => /^https?:\/\//i.test(String(value || "").trim());

const toDDMMYYYY = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const iso = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${String(iso[3]).padStart(2, "0")}/${String(iso[2]).padStart(2, "0")}/${iso[1]}`;

  const slash = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) return `${String(slash[1]).padStart(2, "0")}/${String(slash[2]).padStart(2, "0")}/${slash[3]}`;

  const dash = text.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (dash) return `${String(dash[1]).padStart(2, "0")}/${String(dash[2]).padStart(2, "0")}/${dash[3]}`;

  return text;
};

const toISODateInput = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const iso = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${String(iso[2]).padStart(2, "0")}-${String(iso[3]).padStart(2, "0")}`;

  const slash = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${String(slash[2]).padStart(2, "0")}-${String(slash[1]).padStart(2, "0")}`;

  const dash = text.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (dash) return `${dash[3]}-${String(dash[2]).padStart(2, "0")}-${String(dash[1]).padStart(2, "0")}`;

  return "";
};

const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const buildReadableAddress = (source = {}) => {
  const parts = [
    source?.area_locality || source?.locality || "",
    source?.city || source?.home_address_city || source?.place_name || "",
    source?.region || source?.state || source?.home_address_region || "",
    source?.country || source?.home_address_country || "",
  ].filter(Boolean);

  const composed = parts.join(", ");
  const formatted = source?.formatted_address || "";
  const existing = source?.address_google_map || "";

  if (formatted && !isUrlAddress(formatted)) return formatted;
  if (existing && !isUrlAddress(existing)) return existing;
  if (composed) return composed;
  return "";
};

const isPlusCodePart = (value = "") =>
  /^[A-Z0-9]{4,}\+[A-Z0-9]{2,}/i.test(String(value || "").trim());

const cleanMapDetail = (value = "") => {
  const text = String(value || "").trim();
  return isPlusCodePart(text) ? "" : text;
};

const deriveStreetFromAddress = (address = "") => {
  const parts = String(address || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !isPlusCodePart(part));
  return parts[0] || "";
};

const buildHomeAddressUpdates = (source = {}, pinOverride = "") => {
  const placeName = source?.["place name"] || source?.name || source?.place_name || "";
  const state = source?.state || source?.region || source?.home_address_region || "";
  const city = source?.city || source?.locality || source?.home_address_city || placeName || "";
  const country = source?.country || source?.home_address_country || "";
  const pin = String(
    pinOverride || source?.pin_code || source?.home_address_pincode || source?.post_code || ""
  ).trim();
  const formattedAddress = buildReadableAddress({
    ...source,
    country,
    pin_code: pin,
    city,
    region: state,
  });
  const cityRegionCountry = [city, state, country].filter(Boolean).join(", ");
  const currentCityRegionCountry = [placeName || city, state, country].filter(Boolean).join(", ");

  return {
    basic_pincode_home: pin,
    current_city_region_country: currentCityRegionCountry || cityRegionCountry,
    city_region_country: cityRegionCountry,
    flat_number: cleanMapDetail(source?.flat_number),
    building_name: cleanMapDetail(source?.building_name),
    street_name: cleanMapDetail(source?.street_name) || deriveStreetFromAddress(formattedAddress),
    address_google_map: formattedAddress,
    home_address_latitude: source?.latitude ?? source?.lat ?? source?.position?.lat ?? "",
    home_address_longitude: source?.longitude ?? source?.lng ?? source?.position?.lng ?? "",
    area_locality: source?.area_locality || source?.locality || "",
    home_address_city: city,
    home_address_region: state,
    home_address_country: country,
    home_address_pincode: pin,
  };
};

const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  full_name: "",
  personal_email_id: "",
  work_email_id: "",
  secondary_email: "",
  marital_status: "",
  date_of_birth: "",
  date_of_anniversary: "",
  personal_mobile: "",
  secondary_mobile: "",
  work_mobile: "",
  country_code_personal: "+91",
  country_code_home: "+91",
  work_mobile_country_code: "+91",
  country_code_home_fixed: "+91",
  country_code_work_fixed: "+91",
  home_fixed: "",
  work_fixed: "",
  birth_city_country: "",
  basic_pincode_home: "",
  current_city_region_country: "",
  building_name: "",
  city_region_country: "",
  flat_number: "",
  address_google_map: "",
  street_name: "",
  home_address_latitude: "",
  home_address_longitude: "",
  area_locality: "",
  home_address_city: "",
  home_address_region: "",
  home_address_country: "",
  home_address_pincode: "",
  // validity & type flags — set at runtime by PhoneField
  personal_mobile_valid: false,
  personal_mobile_type: "",
  secondary_mobile_valid: false,
  secondary_mobile_type: "",
  work_mobile_valid: false,
  work_mobile_type: "",
  home_fixed_valid: false,
  home_fixed_type: "",
  work_fixed_valid: false,
  work_fixed_type: "",
};

export default function PersonalInfoPage({ onSave, forceOpenSection, completionLock, onRequestOpenCompletionModal }) {
  const { planType, isPaymentActive, loading: paymentStatusLoading } = usePaymentStatus();

  const [isEditing, setIsEditing] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [openBasic, setOpenBasic] = useState(true);
  const [openAddress, setOpenAddress] = useState(true);
  const [locationModalContext, setLocationModalContext] = useState(null); // 'personal-pincode' | 'address-pincode' | null
  const [mapContext, setMapContext] = useState(null); // 'personal-pincode' | 'address-pincode' | null

  const [currentPhoneField, setCurrentPhoneField] = useState("");
  const [selectedCountryIso, setSelectedCountryIso] = useState({
    personal: "",
    secondary: "",
    work_mobile: "",
  });

  const [sameAsAboveEmail, setSameAsAboveEmail] = useState(false);
  const [sameAsPersonalMobile, setSameAsPersonalMobile] = useState(false);
  const [sameAsPersonalWorkMobile, setSameAsPersonalWorkMobile] = useState(false);
  const [sameAsAboveFixed, setSameAsAboveFixed] = useState(false);

  const [isPersonalExisting, setIsPersonalExisting] = useState(false);
  const [isAddressExisting, setIsAddressExisting] = useState(false);

  const [errors, setErrors] = useState({});
  const formScrollRef = useRef(null);
  const fieldRefs = useRef({});

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showGoogleMapPicker, setShowGoogleMapPicker] = useState(false);
  const [showBirthCityCountryModal, setShowBirthCityCountryModal] = useState(false);
  const [mapInitialSearch, setMapInitialSearch] = useState("");
  const [submittingAll, setSubmittingAll] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isAddressCopied, setIsAddressCopied] = useState(false);
  const [manualEditMode, setManualEditMode] = useState(false);
  const [openedFromModal, setOpenedFromModal] = useState(false);
  const [inlineEditingField, setInlineEditingField] = useState("");
  const [inlineDrafts, setInlineDrafts] = useState({
    personal_mobile: "",
    personal_email_id: "",
    work_email_id: "",
    work_mobile: "",
    secondary_mobile: "",
  });
  const [inlineDraftMeta, setInlineDraftMeta] = useState({});
  const [fieldSaving, setFieldSaving] = useState("");
  const [otpModalState, setOtpModalState] = useState({
    open: false,
    field: "",
    contactLabel: "",
    contactValue: "",
    countryCode: "",
  });
  const copyResetTimeoutRef = useRef(null);
  const addressTextareaRef = useRef(null);
  const lastPincodeToastRef = useRef(0);

  useEffect(() => {
    fetchPersonalInfo();
  }, []);

  useEffect(() => {
    const target = forceOpenSection?.section;
    const nonce = Number(forceOpenSection?.nonce || 0);
    // Only treat as modal-driven when parent explicitly incremented nonce (>0)
    if (!target || nonce <= 0) return;

    setOpenBasic(true);
    setOpenAddress(true);
    setIsEditing(true);
    setManualEditMode(false);
    setOpenedFromModal(true);
  }, [forceOpenSection]);

  // Profile-completion / post-signup lock must open edit mode (not preview)
  useEffect(() => {
    if (!completionLock?.enabled) return;
    setOpenBasic(true);
    setOpenAddress(true);
    setIsEditing(true);
    setManualEditMode(false);
    setOpenedFromModal(true);
  }, [completionLock?.enabled]);

  const isCombinedSubmitMode = manualEditMode || Boolean(completionLock?.enabled);
  const submitScope = isCombinedSubmitMode ? null : (forceOpenSection?.section || null);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const el = addressTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [formData.address_google_map, openAddress, isEditing]);

  const fetchPersonalInfo = async () => {
    try {
      const token = Cookies.get("auth_token");
      const headers = { Authorization: `Bearer ${token}` };

      // Run both fetches in parallel
      const [personalRes, addressRes] = await Promise.allSettled([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/get_personal_info`, { headers }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/get_home_address`, { headers }),
      ]);

      let personal = {};
      let address = {};

      if (personalRes.status === "fulfilled" && personalRes.value.ok) {
        const data = await personalRes.value.json();
        personal = data.data?.[0]?.personal_info || data.data?.[0] || {};
      } else if (personalRes.status === "rejected") {
        }

      if (addressRes.status === "fulfilled" && addressRes.value.ok) {
        const data = await addressRes.value.json();
        address = data.data?.[0]?.home_address || data.data?.[0] || {};
      } else if (addressRes.status === "rejected") {
        }

      // Use INITIAL_FORM as base — avoids stale closure over formData state
      const merged = { ...INITIAL_FORM, ...personal, ...address };
      merged.date_of_birth = toISODateInput(merged.date_of_birth);
      merged.date_of_anniversary = toISODateInput(merged.date_of_anniversary);
      merged.marital_status = VALID_MARITAL_STATUS.has(String(merged.marital_status || "").trim())
        ? String(merged.marital_status).trim()
        : "";
      merged.address_google_map = buildReadableAddress(merged);

      if (merged.personal_mobile) merged.personal_mobile_valid = true;
      if (merged.secondary_mobile) merged.secondary_mobile_valid = true;
      if (merged.work_mobile) merged.work_mobile_valid = true;
      if (merged.home_fixed) merged.home_fixed_valid = true;
      if (merged.work_fixed) merged.work_fixed_valid = true;
      if (!merged.full_name) merged.full_name = [merged.first_name, merged.last_name].filter(Boolean).join(" ");

      setFormData(merged);
      setSameAsAboveEmail(
        Boolean(
          String(merged.personal_email_id || "").trim() &&
          String(merged.personal_email_id || "").trim() === String(merged.work_email_id || "").trim()
        )
      );
      setSameAsPersonalMobile(
        Boolean(
          String(merged.personal_mobile || "").trim() &&
          String(merged.personal_mobile || "").trim() === String(merged.secondary_mobile || "").trim() &&
          String(merged.country_code_personal || "") === String(merged.country_code_home || "")
        )
      );
      setSameAsPersonalWorkMobile(
        Boolean(
          String(merged.personal_mobile || "").trim() &&
          String(merged.personal_mobile || "").trim() === String(merged.work_mobile || "").trim() &&
          String(merged.country_code_personal || "") === String(merged.work_mobile_country_code || "")
        )
      );
      setSameAsAboveFixed(
        Boolean(
          String(merged.home_fixed || "").trim() &&
          String(merged.home_fixed || "").trim() === String(merged.work_fixed || "").trim() &&
          String(merged.country_code_home_fixed || "") === String(merged.country_code_work_fixed || "")
        )
      );
      setIsPersonalExisting(Object.keys(personal).length > 0);
      setIsAddressExisting(Object.keys(address).length > 0);
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = typeof value === "string" ? value.replace(/^\s+/, "") : value;
    const titleCaseFields = new Set(["last_name", "building_name"]);
    const lettersOnlyFields = new Set(["first_name", "last_name"]);
    const toTitleCase = (str) => {
      if (!str) return str;
      return str
        .split(" ")
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
        .join(" ");
    };
    const capitalizeFirstLetter = (str) => {
      if (!str) return str;
      return String(str).replace(/^([a-zA-Z])/, (match) => match.toUpperCase());
    };
    const sanitizedValue = lettersOnlyFields.has(name)
      ? String(normalizedValue || "").replace(/[^A-Za-z\s]/g, "")
      : normalizedValue;
    const processedValue =
      name === "first_name" && sanitizedValue
        ? capitalizeFirstLetter(sanitizedValue)
        : titleCaseFields.has(name) && sanitizedValue
          ? toTitleCase(sanitizedValue)
          : sanitizedValue;

    setFormData((prev) => {
      const updated = { ...prev, [name]: processedValue };
      if (name === "first_name" || name === "last_name") {
        updated.full_name = [
          name === "first_name" ? processedValue : prev.first_name,
          name === "last_name" ? processedValue : prev.last_name,
        ].filter(Boolean).join(" ");
      }
      if (sameAsAboveEmail && name === "personal_email_id") updated.work_email_id = processedValue;
      if (sameAsAboveFixed && name === "home_fixed") updated.work_fixed = processedValue;
      return updated;
    });
  };

  const getInlineFieldValue = (fieldName, useDraft = false) => {
    if (useDraft && Object.prototype.hasOwnProperty.call(inlineDrafts, fieldName)) {
      return inlineDrafts[fieldName];
    }
    if (fieldName === "personal_mobile") return formData.personal_mobile;
    if (fieldName === "personal_email_id") return formData.personal_email_id;
    if (fieldName === "work_email_id") return formData.work_email_id;
    if (fieldName === "work_mobile") return formData.work_mobile;
    if (fieldName === "secondary_mobile") return formData.secondary_mobile;
    return "";
  };

  const getInlineFieldCountryCode = (fieldName) => {
    if (fieldName === "personal_mobile") return formData.country_code_personal || "+91";
    if (fieldName === "work_mobile") return formData.work_mobile_country_code || "+91";
    if (fieldName === "secondary_mobile") return formData.country_code_home || "+91";
    return "";
  };

  const openInlineFieldEditor = (fieldName) => {
    setInlineDrafts((prev) => ({
      ...prev,
      [fieldName]: getInlineFieldValue(fieldName, false),
    }));
    setInlineDraftMeta((prev) => ({
      ...prev,
      [fieldName]: {
        valid: Boolean(formData[`${fieldName}_valid`]),
        numberType: formData[`${fieldName}_type`] || "",
      },
    }));
    setInlineEditingField(fieldName);
  };

  const closeInlineFieldEditor = () => {
    setInlineEditingField("");
    setInlineDraftMeta({});
  };

  const closeOtpModal = () => {
    setOtpModalState({ open: false, field: "", contactLabel: "", contactValue: "", countryCode: "" });
  };

  const openOtpModalForField = (fieldName, value) => {
    const config = INLINE_CONTACT_FIELDS[fieldName];
    if (!config) return;

    setOtpModalState({
      open: true,
      field: fieldName,
      contactLabel: `${config.label}: ${value}`,
      contactValue: value,
      countryCode: config.countryKey ? getInlineFieldCountryCode(fieldName) : "",
    });
  };

  const runInlineFieldCheck = async (fieldName) => {
    const config = INLINE_CONTACT_FIELDS[fieldName];
    if (!config) throw new Error("Unsupported field");

    const value = String(getInlineFieldValue(fieldName, true) || "").trim();
    if (!value) {
      throw new Error(`Enter ${config.label}`);
    }

    if ((fieldName === "personal_email_id" || fieldName === "work_email_id") && !isEmail(value)) {
      throw new Error("Enter a valid email");
    }

    const draftMeta = inlineDraftMeta[fieldName] || {};

    if (fieldName === "personal_mobile" && !draftMeta.valid) {
      throw new Error("Please enter a valid mobile number");
    }

    if (fieldName === "work_mobile" && !draftMeta.valid) {
      throw new Error("Please enter a valid mobile number");
    }

    if (fieldName === "secondary_mobile" && !draftMeta.valid) {
      throw new Error("Please enter a valid mobile number");
    }

    const payload = new URLSearchParams({
      [config.bodyKey]: value,
    });

    if (config.countryKey) {
      payload.set(config.countryKey, String(getInlineFieldCountryCode(fieldName) || "").trim());
    }

    const token = Cookies.get("auth_token");
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${config.endpointBase}/check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
      body: payload.toString(),
    });

    const data = await res.json();
    if (String(data?.status) !== "1") {
      throw new Error(data?.message || `${config.label} already exists`);
    }

    return value;
  };

  const handleSaveInlineField = async (fieldName) => {
    const config = INLINE_CONTACT_FIELDS[fieldName];
    if (!config) return;

    setFieldSaving(fieldName);
    try {
      const checkedValue = await runInlineFieldCheck(fieldName);
      const token = Cookies.get("auth_token");
      const payload = new URLSearchParams({
        [config.bodyKey]: checkedValue,
      });

      if (config.countryKey) {
        payload.set(config.countryKey, String(getInlineFieldCountryCode(fieldName) || "").trim());
      }

      const sendRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}${config.endpointBase}/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: payload.toString(),
      });

      const sendData = await sendRes.json();
      if (String(sendData?.status) !== "1") {
        throw new Error(sendData?.message || "Failed to send OTP");
      }

      toast.success(sendData?.message || "OTP sent successfully");
      openOtpModalForField(fieldName, checkedValue);
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || "Unable to save field");
    } finally {
      setFieldSaving("");
    }
  };

  const handleVerifyInlineOtp = async (otpValue) => {
    const { field, contactValue, countryCode } = otpModalState;
    const config = INLINE_CONTACT_FIELDS[field];
    if (!config) return;

    const token = Cookies.get("auth_token");
    const payload = new URLSearchParams({
      [config.bodyKey]: contactValue,
      otp: otpValue,
    });

    if (config.countryKey) {
      payload.set(config.countryKey, String(countryCode || "").trim());
    }

    setFieldSaving(field);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${config.endpointBase}/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: payload.toString(),
      });

      const data = await res.json();
      if (String(data?.status) !== "1") {
        throw new Error(data?.message || "Invalid OTP");
      }

      updateAuthTokenFromResponse(data);

      const committedValue = String(otpModalState.contactValue || "").trim();
      setFormData((prev) => ({
        ...prev,
        [field]: committedValue,
        ...(field === "personal_mobile"
          ? { country_code_personal: String(countryCode || getInlineFieldCountryCode(field) || "+91") }
          : {}),
        ...(field === "work_mobile"
          ? { work_mobile_country_code: String(countryCode || getInlineFieldCountryCode(field) || "+91") }
          : {}),
        ...(field === "secondary_mobile"
          ? { country_code_home: String(countryCode || getInlineFieldCountryCode(field) || "+91") }
          : {}),
        ...(field === "personal_mobile"
          ? { personal_mobile_valid: true, personal_mobile_type: inlineDraftMeta[field]?.numberType || "" }
          : {}),
        ...(field === "work_mobile"
          ? { work_mobile_valid: true, work_mobile_type: inlineDraftMeta[field]?.numberType || "" }
          : {}),
        ...(field === "secondary_mobile"
          ? { secondary_mobile_valid: true, secondary_mobile_type: inlineDraftMeta[field]?.numberType || "" }
          : {}),
      }));
      setInlineDrafts((prev) => ({ ...prev, [field]: committedValue }));
      toast.success(data?.message || "OTP verified successfully");
      closeOtpModal();
      closeInlineFieldEditor();
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || "OTP verification failed");
    } finally {
      setFieldSaving("");
    }
  };

  const getInlineActionButton = (fieldName) => {
    const isActive = inlineEditingField === fieldName;
    const isSaving = fieldSaving === fieldName;

    return (
      <button
        type="button"
        onClick={() => {
          if (isSaving) return;
          if (!isEditing) {
            setManualEditMode(true);
            setIsEditing(true);
          }
          if (isActive) {
            handleSaveInlineField(fieldName);
            return;
          }
          openInlineFieldEditor(fieldName);
        }}
        className="inline-flex items-center gap-1 rounded-full bg-[#F3F0FF] px-3 py-1 text-[11px] font-semibold text-[#6938EF] shadow-sm transition hover:bg-[#E7DFFF]"
        aria-label={isActive ? `Save ${fieldName}` : `Edit ${fieldName}`}
      >
        {isSaving ? (
          <>
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#6938EF] border-t-transparent" />
            Saving
          </>
        ) : isActive ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
              <path d="M9 16.17l-3.88-3.88 1.41-1.41L9 13.34l8.47-8.47 1.41 1.41z" />
            </svg>
            Save
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
              <path d="M3 17.25V21h3.75l11-11.03-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.09.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
            </svg>
            Edit
          </>
        )}
      </button>
    );
  };

  const updateInlineDraft = (fieldName, value, meta = {}) => {
    setInlineDrafts((prev) => ({ ...prev, [fieldName]: value }));
    if (Object.keys(meta).length > 0) {
      setInlineDraftMeta((prev) => ({ ...prev, [fieldName]: { ...(prev[fieldName] || {}), ...meta } }));
    }
  };

  const handleToggleSameAsAboveEmail = () => {
    setSameAsAboveEmail((prev) => {
      const next = !prev;
      setFormData((fd) => ({ ...fd, work_email_id: next ? fd.personal_email_id : "" }));
      setErrors((err) => ({ ...err, work_email_id: null }));
      if (next && inlineEditingField === "work_email_id") {
        closeInlineFieldEditor();
      }
      return next;
    });
  };

  const handleToggleSameAsPersonalMobile = () => {
    setSameAsPersonalMobile((prev) => {
      const next = !prev;
      setFormData((fd) => ({
        ...fd,
        secondary_mobile: next ? fd.personal_mobile : "",
        country_code_home: next ? fd.country_code_personal : (fd.country_code_home || "+91"),
        secondary_mobile_valid: next ? fd.personal_mobile_valid : false,
        secondary_mobile_type: next ? fd.personal_mobile_type : "",
      }));
      setErrors((err) => ({ ...err, secondary_mobile: null }));
      return next;
    });
  };

  const handleToggleSameAsPersonalWorkMobile = () => {
    setSameAsPersonalWorkMobile((prev) => {
      const next = !prev;
      setFormData((fd) => ({
        ...fd,
        work_mobile: next ? fd.personal_mobile : "",
        work_mobile_country_code: next ? (fd.country_code_personal || "+91") : (fd.work_mobile_country_code || "+91"),
        work_mobile_valid: next ? (fd.personal_mobile_valid || !!String(fd.personal_mobile || "").trim()) : false,
        work_mobile_type: next ? fd.personal_mobile_type : "",
      }));
      setErrors((err) => ({ ...err, work_mobile: null }));
      if (next && inlineEditingField === "work_mobile") {
        closeInlineFieldEditor();
      }
      return next;
    });
  };

  const handleToggleSameAsAboveFixed = () => {
    setSameAsAboveFixed((prev) => {
      const next = !prev;
      setFormData((fd) => ({
        ...fd,
        work_fixed: next ? fd.home_fixed : "",
        country_code_work_fixed: next ? fd.country_code_home_fixed : "+91",
        work_fixed_valid: next ? fd.home_fixed_valid : false,
        work_fixed_type: next ? fd.home_fixed_type : "",
      }));
      setErrors((err) => ({ ...err, work_fixed: null }));
      return next;
    });
  };

  const openBirthCityCountryModal = () => {
    if (!isEditing || planType < 2) return;
    setShowBirthCityCountryModal(true);
  };

  const closeBirthCityCountryModal = () => {
    setShowBirthCityCountryModal(false);
  };

  const handleBirthCityCountrySelect = (item) => {
    const value = item?.birth_city_country || item?.description || item?.main_text || "";
    if (!value) return;

    setFormData((prev) => ({
      ...prev,
      birth_city_country: value,
    }));
    setErrors((prev) => ({ ...prev, birth_city_country: null }));
    toast.success("Birth city selected");
  };

  const openLocationModalForPincode = (context) => {
    if (!isEditing) return;
    setLocationModalContext(context);
    setShowLocationModal(true);
  };

  const openGoogleMapFromAddressField = () => {
    if (!isEditing) return;
    setMapContext("address-pincode");
    const seed = [
      formData.basic_pincode_home,
      formData.current_city_region_country,
      formData.address_google_map,
    ].filter(Boolean).join(", ");
    setMapInitialSearch(seed);
    setShowGoogleMapPicker(true);
  };

  const fillAddressFromSelection = ({ place, country, post_code }, { preserveFlatNumber = false } = {}) => {
    const updates = buildHomeAddressUpdates(
      { ...place, country },
      post_code || place?.pin_code || ""
    );
    if (preserveFlatNumber) {
      delete updates.flat_number;
    }
    setFormData((prev) => ({ ...prev, ...updates }));
    setErrors((prev) => ({
      ...prev,
      basic_pincode_home: null,
      current_city_region_country: null,
      address_google_map: null,
      home_address_city: null,
      home_address_region: null,
      home_address_country: null,
      home_address_pincode: null,
    }));
  };

  // Called when user picks a place in LocationModal
  const handlePlaceSelect = ({ place, country, post_code }) => {
    setShowLocationModal(false);
    const isPersonalPincode = locationModalContext === "personal-pincode";
    const isAddressPincode = locationModalContext === "address-pincode";

    if (isPersonalPincode || isAddressPincode) {
      fillAddressFromSelection(
        { place, country, post_code },
        { preserveFlatNumber: isPersonalPincode }
      );
      toast.success(
        isPersonalPincode
          ? "Personal info updated from location!"
          : "Address updated from location!"
      );
    }

    setLocationModalContext(null);
  };

  // Called when user confirms a pin on the Google Map
  const handleLocationSelect = (item) => {
    const previousPin = String(formData.basic_pincode_home || formData.home_address_pincode || "");
    const updates = buildHomeAddressUpdates(item);
    const selectedPin = String(updates.home_address_pincode || "");
    const pinChanged = Boolean(selectedPin) && selectedPin !== previousPin;

    setFormData((prev) => ({ ...prev, ...updates }));
    setErrors((prev) => ({
      ...prev,
      basic_pincode_home: null,
      current_city_region_country: null,
      address_google_map: null,
      home_address_city: null,
      home_address_region: null,
      home_address_country: null,
      home_address_pincode: null,
    }));

    if (pinChanged) {
      toast.success(`Address updated. Pincode changed to ${selectedPin}.`);
    } else {
      toast.success("Address updated from map!");
    }

    setShowGoogleMapPicker(false);
    setMapContext(null);
    setMapInitialSearch("");
    setIsEditing(true);
  };

  const handleCountrySelect = (country) => {
    const dial = `+${country.dialCode}`;
    const phoneCodeKeyMap = {
      personal: "country_code_personal",
      secondary: "country_code_home",
      work_mobile: "work_mobile_country_code",
      home_fixed: "country_code_home_fixed",
      work_fixed: "country_code_work_fixed",
    };
    const codeField = phoneCodeKeyMap[currentPhoneField];
    if (!codeField) {
      setShowCountryModal(false);
      return;
    }

    setFormData((prev) => ({ ...prev, [codeField]: dial }));
    setSelectedCountryIso((prev) => ({ ...prev, [currentPhoneField]: country.code }));
    setShowCountryModal(false);
  };

  const handleCopyAddress = async () => {
    const addressText = String(formData.address_google_map || "").trim();
    if (!addressText) {
      toast.error("No address to copy");
      return;
    }

    const showCopiedState = () => {
      setIsAddressCopied(true);
      if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
      copyResetTimeoutRef.current = setTimeout(() => {
        setIsAddressCopied(false);
      }, 1500);
    };

    try {
      await navigator.clipboard.writeText(addressText);
      toast.success("Address copied");
      showCopiedState();
    } catch {
      const tempTextArea = document.createElement("textarea");
      tempTextArea.value = addressText;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand("copy");
      document.body.removeChild(tempTextArea);
      toast.success("Address copied");
      showCopiedState();
    }
  };

  // Shared scroll-to-first-error logic used by both submit handlers
  const scrollToFirstError = (errs) => {
    
    const firstKey = Object.keys(errs)[0];
    const el = fieldRefs.current[firstKey];
    const container = formScrollRef.current;
    if (el) {
      const input = el.querySelector("input, select, textarea");
      if (container) {
        const offsetTop = el.offsetTop - 24;
        container.scrollTo({ top: offsetTop < 0 ? 0 : offsetTop, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (input && typeof input.focus === "function") input.focus();
    }
    toast.error("Please fix the highlighted fields");
  };

  const handlePersonalSubmit = async () => {
    const newErrors = {};

    if (!String(formData.first_name || "").trim()) newErrors.first_name = "Enter First Name";
    if (!String(formData.last_name || "").trim()) newErrors.last_name = "Enter Last Name";
    if (!String(formData.personal_email_id || "").trim()) newErrors.personal_email_id = "Enter Personal Email";
    if (!String(formData.work_email_id || "").trim()) newErrors.work_email_id = "Enter Work Email";
    if (!String(formData.personal_mobile || "").trim()) newErrors.personal_mobile = "Enter Personal Mobile";
    if (!sameAsPersonalWorkMobile && !String(formData.work_mobile || "").trim()) newErrors.work_mobile = "Enter Work Mobile";
    if (!String(formData.marital_status || "").trim()) newErrors.marital_status = "Select Marital Status";
    if (!String(formData.date_of_birth || "").trim()) newErrors.date_of_birth = "Enter Date of Birth";
    if (String(formData.marital_status || "").trim() === "Married" && !String(formData.date_of_anniversary || "").trim()) {
      newErrors.date_of_anniversary = "Enter Anniversary Date";
    }
    if (planType >= 2 && !String(formData.birth_city_country || "").trim()) newErrors.birth_city_country = "Enter Birth City and Country";
    if (planType >= 2 && !String(formData.basic_pincode_home || "").trim()) newErrors.basic_pincode_home = "Enter Pincode - Home";
    if (planType >= 2 && !String(formData.current_city_region_country || "").trim()) newErrors.current_city_region_country = "Enter Current City, Region and Country";

    if (formData.personal_email_id && !isEmail(formData.personal_email_id))
      newErrors.personal_email_id = "Enter a valid email";
    if (formData.work_email_id && !isEmail(formData.work_email_id))
      newErrors.work_email_id = "Enter a valid email";

    if (!formData.personal_mobile_valid)
      newErrors.personal_mobile = "Please enter a valid mobile number";
    if (formData.secondary_mobile && !formData.secondary_mobile_valid)
      newErrors.secondary_mobile = "Please enter a valid mobile number";
    if (!sameAsPersonalWorkMobile && formData.work_mobile && !formData.work_mobile_valid)
      newErrors.work_mobile = "Please enter a valid mobile number";
    if (formData.home_fixed && !formData.home_fixed_valid)
      newErrors.home_fixed = "Please enter a valid landline number";
    if (formData.work_fixed && !formData.work_fixed_valid)
      newErrors.work_fixed = "Please enter a valid landline number";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
      return;
    }

    try {
      const token = Cookies.get("auth_token");
      const payload = new URLSearchParams();
      // If a number field is blank, send blank country code as well
      const temp = { ...formData };
      if (!String(temp.personal_mobile || "").trim()) temp.country_code_personal = "";
      if (!String(temp.secondary_mobile || "").trim()) temp.country_code_home = "";
      if (!String(temp.work_mobile || "").trim()) temp.work_mobile_country_code = "";
      if (!String(temp.home_fixed || "").trim()) temp.country_code_home_fixed = "";
      if (!String(temp.work_fixed || "").trim()) temp.country_code_work_fixed = "";
      PERSONAL_KEYS.forEach((k) => payload.append(k, temp[k] ?? ""));
      payload.set("date_of_birth", toDDMMYYYY(temp.date_of_birth));
      payload.set("date_of_anniversary", toDDMMYYYY(temp.date_of_anniversary));

      const endpoint = isPersonalExisting ? "/update_personal_info" : "/insert_personal_info";
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: payload.toString(),
      });
      const data = await res.json();

      if (data.status === "1") {
        updateAuthTokenFromResponse(data);
        toast.success(isPersonalExisting ? "Personal info updated successfully!" : "Personal info saved successfully!");
        setIsPersonalExisting(true);
        setIsEditing(false);
        setErrors({});
        if (typeof onSave === "function") onSave();
      } else {
        toast.dismiss();
        toast.error(data.message || "Failed to save");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    }
  };

  const handleAddressSubmit = async () => {
    const newErrors = {};

    if (planType >= 2) {
      if (!formData.birth_city_country?.trim()) newErrors.birth_city_country = "Enter Birth City and Country";
      if (!formData.basic_pincode_home?.trim()) newErrors.basic_pincode_home = "Enter Pincode - Home";
      if (!formData.current_city_region_country?.trim()) newErrors.current_city_region_country = "Enter Current City, Region and Country";
      if (!formData.flat_number?.trim()) newErrors.flat_number = "Enter Flat Number";
      if (!formData.building_name?.trim()) newErrors.building_name = "Enter Building Name";
      if (!formData.address_google_map?.trim()) newErrors.address_google_map = "Enter Address";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
      return;
    }

    try {
      const token = Cookies.get("auth_token");
      const payload = new URLSearchParams();
      ADDRESS_KEYS.forEach((k) => payload.append(k, formData[k] ?? ""));

      const endpoint = isAddressExisting ? "/update_home_address" : "/insert_home_address";
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: payload.toString(),
      });
      const data = await res.json();

      if (data.status === "1") {
        updateAuthTokenFromResponse(data);
        toast.success(isAddressExisting ? "Home address updated successfully!" : "Home address saved successfully!");
        setIsAddressExisting(true);
        setIsEditing(false);
        setErrors({});
        if (typeof onSave === "function") onSave();
      } else {
        toast.dismiss();
        toast.error(data.message || "Failed to save");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    }
  };

  const handleSubmitCurrentSection = async () => {
    if (submitScope === "basic") {
      await handlePersonalSubmit();
      return;
    }

    if (submitScope === "address") {
      await handleAddressSubmit();
      return;
    }

    await handleSubmitAll();
  };

  const handleSubmitAll = async () => {
    const mergedErrors = {};

    if (!String(formData.first_name || "").trim()) mergedErrors.first_name = "Enter First Name";
    if (!String(formData.last_name || "").trim()) mergedErrors.last_name = "Enter Last Name";
    if (!String(formData.personal_email_id || "").trim()) mergedErrors.personal_email_id = "Enter Personal Email";
    if (!String(formData.work_email_id || "").trim()) mergedErrors.work_email_id = "Enter Work Email";
    if (!String(formData.personal_mobile || "").trim()) mergedErrors.personal_mobile = "Enter Personal Mobile";
    if (!sameAsPersonalWorkMobile && !String(formData.work_mobile || "").trim()) mergedErrors.work_mobile = "Enter Work Mobile";
    if (!String(formData.marital_status || "").trim()) mergedErrors.marital_status = "Select Marital Status";
    if (!String(formData.date_of_birth || "").trim()) mergedErrors.date_of_birth = "Enter Date of Birth";
    if (String(formData.marital_status || "").trim() === "Married" && !String(formData.date_of_anniversary || "").trim()) {
      mergedErrors.date_of_anniversary = "Enter Anniversary Date";
    }
    if (planType >= 2 && !String(formData.birth_city_country || "").trim()) mergedErrors.birth_city_country = "Enter Birth City and Country";
    if (planType >= 2 && !String(formData.basic_pincode_home || "").trim()) mergedErrors.basic_pincode_home = "Enter Pincode - Home";
    if (planType >= 2 && !String(formData.current_city_region_country || "").trim()) mergedErrors.current_city_region_country = "Enter Current City, Region and Country";
    if (planType >= 2 && !String(formData.flat_number || "").trim()) mergedErrors.flat_number = "Enter Flat Number / House Number";
    if (planType >= 2 && !String(formData.building_name || "").trim()) mergedErrors.building_name = "Enter Building Name / House Name";
    if (planType >= 2 && !String(formData.address_google_map || "").trim()) mergedErrors.address_google_map = "Enter Address";

    if (formData.personal_email_id && !isEmail(formData.personal_email_id))
      mergedErrors.personal_email_id = "Enter a valid email";
    if (formData.work_email_id && !isEmail(formData.work_email_id))
      mergedErrors.work_email_id = "Enter a valid email";

    if (!formData.personal_mobile_valid)
      mergedErrors.personal_mobile = "Please enter a valid mobile number";
    if (formData.secondary_mobile && !formData.secondary_mobile_valid)
      mergedErrors.secondary_mobile = "Please enter a valid mobile number";
    if (!sameAsPersonalWorkMobile && formData.work_mobile && !formData.work_mobile_valid)
      mergedErrors.work_mobile = "Please enter a valid mobile number";
    if (formData.home_fixed && !formData.home_fixed_valid)
      mergedErrors.home_fixed = "Please enter a valid landline number";
    if (formData.work_fixed && !formData.work_fixed_valid)
      mergedErrors.work_fixed = "Please enter a valid landline number";

    if (planType >= 2) {
      if (!formData.flat_number?.trim()) mergedErrors.flat_number = "Enter Flat Number";
      if (!formData.building_name?.trim()) mergedErrors.building_name = "Enter Building Name";
      if (!formData.address_google_map?.trim()) mergedErrors.address_google_map = "Enter Address";
    }

    setErrors(mergedErrors);
    if (Object.keys(mergedErrors).length > 0) {
      scrollToFirstError(mergedErrors);
      return;
    }

    setSubmittingAll(true);
    try {
      const token = Cookies.get("auth_token");
      const headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      };

      const personalPayload = new URLSearchParams();
      const tempPersonal = { ...formData };
      if (!String(tempPersonal.personal_mobile || "").trim()) tempPersonal.country_code_personal = "";
      if (!String(tempPersonal.secondary_mobile || "").trim()) tempPersonal.country_code_home = "";
      if (!String(tempPersonal.work_mobile || "").trim()) tempPersonal.work_mobile_country_code = "";
      if (!String(tempPersonal.home_fixed || "").trim()) tempPersonal.country_code_home_fixed = "";
      if (!String(tempPersonal.work_fixed || "").trim()) tempPersonal.country_code_work_fixed = "";
      PERSONAL_KEYS.forEach((k) => personalPayload.append(k, tempPersonal[k] ?? ""));
      personalPayload.set("date_of_birth", toDDMMYYYY(tempPersonal.date_of_birth));
      personalPayload.set("date_of_anniversary", toDDMMYYYY(tempPersonal.date_of_anniversary));
      const personalEndpoint = isPersonalExisting ? "/update_personal_info" : "/insert_personal_info";

      const addressPayload = new URLSearchParams();
      ADDRESS_KEYS.forEach((k) => addressPayload.append(k, formData[k] ?? ""));
      const addressEndpoint = isAddressExisting ? "/update_home_address" : "/insert_home_address";

      const [personalRes, addressRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}${personalEndpoint}`, {
          method: "POST",
          headers,
          body: personalPayload.toString(),
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}${addressEndpoint}`, {
          method: "POST",
          headers,
          body: addressPayload.toString(),
        }),
      ]);

      const [personalData, addressData] = await Promise.all([
        personalRes.json(),
        addressRes.json(),
      ]);

      const personalOk = personalData?.status === "1";
      const addressOk = addressData?.status === "1";

      if (personalOk && addressOk) {
        updateAuthTokenFromResponse(personalData);
        updateAuthTokenFromResponse(addressData);
        toast.success("Personal info and home address saved successfully!");
        setIsPersonalExisting(true);
        setIsAddressExisting(true);
        setIsEditing(false);
        setErrors({});
        if (typeof onSave === "function") onSave();
        return;
      }

      if (!personalOk) toast.error(personalData?.message || "Failed to save personal info");
      if (!addressOk) toast.error(addressData?.message || "Failed to save home address");
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    } finally {
      setSubmittingAll(false);
    }
  };

  const hasBasicData = useMemo(
    () =>
      [
        formData.first_name, formData.last_name, formData.personal_email_id,
        formData.work_email_id, formData.secondary_email, formData.marital_status,
        formData.date_of_birth, formData.date_of_anniversary, formData.personal_mobile,
        formData.secondary_mobile, formData.work_mobile, formData.home_fixed,
        formData.work_fixed, formData.birth_city_country, formData.current_city_region_country,
      ].some((v) => v && v.toString().trim()),
    [formData]
  );

  const hasAddressData = useMemo(
    () =>
      [
        formData.flat_number, formData.building_name, formData.address_google_map,
      ].some((v) => v && v.toString().trim()),
    [formData]
  );

  const isPincodeDisabledByAddress = Boolean(String(formData.address_google_map || "").trim());
  
  // Preview helpers: used when `isEditing` is false to render a compact read-only view
  const getPreviewValue = (fieldName) => {
    if (fieldName === "home_address_pincode") {
      return String(formData.home_address_pincode || formData.basic_pincode_home || "");
    }
    if (fieldName === "street_name") {
      return String(formData.street_name || deriveStreetFromAddress(formData.address_google_map) || "");
    }
    const value = formData[fieldName];
    if (!value && value !== 0) return "";
    if (fieldName === "date_of_birth" || fieldName === "date_of_anniversary") return toDDMMYYYY(value);
    if (fieldName === "personal_mobile") return `${formData.country_code_personal || ""} ${value}`.trim();
    if (fieldName === "secondary_mobile") return `${formData.country_code_home || ""} ${value}`.trim();
    if (fieldName === "work_mobile") return `${formData.work_mobile_country_code || ""} ${value}`.trim();
    if (fieldName === "home_fixed") return `${formData.country_code_home_fixed || ""} ${value}`.trim();
    if (fieldName === "work_fixed") return `${formData.country_code_work_fixed || ""} ${value}`.trim();
    return String(value || "");
  };

  // Full preview field configuration to mirror friend component layout
  const previewFieldConfig = [
    { key: 'first_name', label: 'First Name', section: 'basic', icon: '../input-logos/user.svg' },
    { key: 'last_name', label: 'Last Name', section: 'basic', icon: '../input-logos/user.svg' },
    { key: 'full_name', label: 'Full Name', section: 'basic', icon: '../input-logos/user.svg' },
    { key: 'personal_email_id', label: 'Personal Email Id', section: 'basic', icon: '../input-logos/sms.svg' },
    { key: 'work_email_id', label: 'Work Email Id', section: 'basic', icon: '../input-logos/sms.svg' },
    { key: 'secondary_email', label: 'Secondary Email', section: 'basic', icon: '../input-logos/sms.svg' },
    { key: 'personal_mobile', label: 'Personal Mobile', section: 'basic', icon: '../input-logos/call.svg' },
    { key: 'work_mobile', label: 'Work Mobile', section: 'basic', icon: '../input-logos/call.svg' },
    { key: 'secondary_mobile', label: 'Secondary Mobile', section: 'basic', icon: '../input-logos/call.svg' },
    { key: 'home_fixed', label: 'Home Fixed', section: 'basic', icon: '../input-logos/call.svg' },
    { key: 'work_fixed', label: 'Work Fixed', section: 'basic', icon: '../input-logos/call.svg' },
    { key: 'date_of_birth', label: 'Date of Birth', section: 'basic', icon: '../input-logos/calander.svg' },
    { key: 'marital_status', label: 'Marital Status', section: 'basic', icon: '../input-logos/ring.svg' },
    { key: 'date_of_anniversary', label: 'Anniversary Date', section: 'basic', icon: '../input-logos/calander.svg' },
    { key: 'birth_city_country', label: 'Birth City and Country', section: 'basic', icon: '../input-logos/location.svg' },
    { key: 'basic_pincode_home', label: 'Pincode - Home', section: 'basic', icon: '../input-logos/location.svg' },
    { key: 'current_city_region_country', label: 'Current City, Region and Country', section: 'basic', icon: '../input-logos/location.svg' },

    { key: 'flat_number', label: 'Flat Number / House Number', section: 'address', icon: '../input-logos/building.svg' },
    { key: 'building_name', label: 'Building Name / House Name', section: 'address', icon: '../input-logos/building.svg' },
    { key: 'address_google_map', label: 'Address', section: 'address', icon: '../input-logos/location.svg' },
  ];

  const basicPreviewFields = previewFieldConfig.filter((f) => f.section === 'basic');
  const addressPreviewFields = previewFieldConfig.filter((f) => f.section === 'address');

  const renderPreviewFieldCard = (field) => {
    const value = getPreviewValue(field.key);
    if (!value) return null;

    // Address field spans both columns and includes copy action
    if (field.key === "address_google_map") {
      return (
        <div key={field.key} className="min-[1150px]:col-span-2 px-4 py-4 border-b last:border-b-0">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 text-[#6938EF]">
              {field.icon ? (
                <img src={field.icon} alt="" className="w-4 h-4" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 10c0 6-9 12-9 12S3 16 3 10a9 9 0 1 1 18 0z" stroke="#6938EF" strokeWidth="1.2" fill="none"/>
                  <circle cx="12" cy="10" r="2" stroke="#6938EF" strokeWidth="1.2" fill="none"/>
                </svg>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-500">{field.label}</div>
                  <div className="text-sm font-medium text-gray-900 break-words">{value || '—'}</div>
                </div>
                {String(value || "").trim() && (
                  <button
                    type="button"
                    onClick={() => handleCopyAddress(value)}
                    className="text-xs font-medium text-[#6938EF] hover:text-[#5a2fd6]"
                  >
                    {isAddressCopied ? "Copied!" : "Copy"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Standard single-field row matching friend component layout
    return (
      <div key={field.key} className="flex items-center gap-3 px-4 py-4 border-b last:border-b-0">
        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 text-[#6938EF]">
          {field.icon ? (
            <img src={field.icon} alt="" className="w-4 h-4" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12h18" stroke="#6938EF" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-xs text-gray-500">{field.label.replace(/Id$/,'')}</div>
          <div className="text-sm font-medium text-gray-900 break-words">{value || '—'}</div>
        </div>
      </div>
    );
  };
  if (paymentStatusLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AF8]" />
      </div>
    );
  }

  if (!isPaymentActive) return (
    <div className="w-full flex items-center justify-center h-[90vh] p-6">
      <div className="max-w-5xl w-full bg-white rounded-2xl p-8 shadow-lg grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        {/* Left column: text, perks, CTA */}
        <div className="px-2 md:px-6">
          <div className="w-20 h-20 rounded-full bg-[#F6F4FF] flex items-center justify-center mb-4">
            <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="g1a" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#BFA7FF"/>
                  <stop offset="100%" stopColor="#7B4DFF"/>
                </linearGradient>
              </defs>
              <circle cx="40" cy="55" r="15" fill="url(#g1a)"/>
              <circle cx="80" cy="55" r="15" fill="url(#g1a)"/>
            </svg>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-2">No Active Plan</h3>
          <p className="text-gray-600 mb-6">You don't have an active plan yet. Choose a plan to unlock all the features and make the most of  PhoneBookPlus.</p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#BFA7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <circle cx="40" cy="55" r="15" fill="url(#icon1)"/>
                <circle cx="80" cy="55" r="15" fill="url(#icon1)"/>
                <path d="M35 25C50 10 70 10 85 25" stroke="#8D63FF" strokeWidth="6" strokeLinecap="round"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Build stronger connections</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon2" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#CDB7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <path d="M60 10L100 25V55C100 82 82 102 60 110C38 102 20 82 20 55V25L60 10Z" fill="url(#icon2)"/>
                <rect x="42" y="50" width="36" height="28" rx="6" fill="#8D63FF"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Keep your data secure</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon3" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#D5C4FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <rect x="20" y="65" width="18" height="35" rx="4" fill="url(#icon3)"/>
                <rect x="48" y="50" width="18" height="50" rx="4" fill="url(#icon3)"/>
                <rect x="76" y="35" width="18" height="65" rx="4" fill="url(#icon3)"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Boost productivity and grow</p>
            </div>
          </div>

          <button onClick={() => window.location.href = "/payment"} className="mt-2 inline-flex items-center gap-3 bg-[#6D5EF7] text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:bg-[#5b49e8] transition">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Choose a Plan
          </button>
        </div>

        {/* Right column: illustration */}
        <div className="flex items-center justify-center p-4">
          <svg width="320" height="240" viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-w-full h-auto">
            <defs>
              <linearGradient id="illGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#F3EEFF"/>
                <stop offset="100%" stopColor="#F8F6FF"/>
              </linearGradient>
            </defs>
            <rect x="20" y="20" width="220" height="200" rx="16" fill="url(#illGrad)" stroke="#EDE7FF" strokeWidth="2"/>
            <rect x="52" y="44" width="156" height="14" rx="7" fill="#FFFFFF"/>
            <rect x="52" y="72" width="156" height="12" rx="6" fill="#EDE7FF"/>
            <rect x="52" y="96" width="120" height="12" rx="6" fill="#EDE7FF"/>
            <circle cx="260" cy="40" r="28" fill="#F6EEFF"/>
          </svg>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {showCountryModal && (
        <CountryModel
          onSelect={handleCountrySelect}
          onClose={() => setShowCountryModal(false)}
        />
      )}

      {showLocationModal && (
        <LocationModal
          show={showLocationModal}
          onClose={() => { setShowLocationModal(false); setLocationModalContext(null); }}
          onSelect={handlePlaceSelect}
          type="home"
        />
      )}

      {showGoogleMapPicker && (
        <>
          <GoogleMapPicker
            show={showGoogleMapPicker}
            onClose={() => { setShowGoogleMapPicker(false); setMapContext(null); setMapInitialSearch(""); }}
            onSelect={handleLocationSelect}
            initialSearch={mapInitialSearch}
          />
          {/* <MapplsMapPicker
            show={showGoogleMapPicker}
            onClose={() => { setShowGoogleMapPicker(false); setMapContext(null); setMapInitialSearch(""); }}
            onSelect={handleLocationSelect}
            initialSearch={mapInitialSearch}
          /> */}
        </>
      )}

      {showBirthCityCountryModal && (
        <BirthCityCountryModal
          show={showBirthCityCountryModal}
          onClose={closeBirthCityCountryModal}
          onSelect={handleBirthCityCountrySelect}
        />
      )}

      <OtpModal
        open={otpModalState.open}
        title={`Verify ${INLINE_CONTACT_FIELDS[otpModalState.field]?.label || "Contact"}`}
        description="Enter the OTP sent to confirm this change."
        contactLabel={otpModalState.contactLabel}
        loading={fieldSaving === otpModalState.field}
        onClose={closeOtpModal}
        onVerify={handleVerifyInlineOtp}
      />

      <div className="rounded-2xl md:p-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {isEditing && (
              <button
                type="button"
                onClick={() => {
                  if (openedFromModal) {
                    if (typeof onRequestOpenCompletionModal === "function") onRequestOpenCompletionModal();
                    return;
                  }
                  setManualEditMode(false);
                  setIsEditing(false);
                }}
                aria-label="Back to view mode"
                className="h-8 w-8 text-[#6938EF]  flex items-center justify-center"
              >
                  <img src="./back-arrow.svg" alt="back-arrow" className="sm:mr-4" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">Personal Info</h2>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <button
                onClick={handleSubmitCurrentSection}
                type="button"
                disabled={submittingAll}
                aria-busy={submittingAll}
                aria-label={submitScope === "basic" ? "Submit personal info" : submitScope === "address" ? "Submit address" : "Submit personal and address"}
                className={`h-10 w-10 rounded-full flex items-center justify-center ${submittingAll ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <img src="../tick_mark.svg" alt="Submit" className="w-6 h-6" />
              </button>
            ) : (
              <button
                onClick={() => {
                  setManualEditMode(true);
                  setOpenedFromModal(false);
                  setIsEditing(true);
                }}
                type="button"
              >
                <img src="./edit.svg" alt="Edit" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 md:h-[83dvh] md:overflow-y-auto md:pb-0 [&::-webkit-scrollbar]:hidden" ref={formScrollRef}>

          {/* Section 1: Basic Details */}
          {(isEditing || hasBasicData) && (
            <div className="mb-4 border border-gray-100 rounded-xl bg-white">
              <button
                type="button"
                onClick={() => setOpenBasic((prev) => !prev)}
                className={`sticky top-0 z-30 w-full flex items-center justify-between px-5 py-4 transition-all bg-white ${
                  openBasic
                    ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                    : "bg-gradient-to-r from-gray-50 to-white"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${openBasic ? "bg-[#6938EF]/10" : "bg-gray-200/50"}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="8" r="4" stroke="#6938EF" strokeWidth="2" />
                      <path d="M6 20c0-2.21 2.686-4 6-4s6 1.79 6 4" stroke="#6938EF" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <span className={`font-semibold transition-colors ${openBasic ? "text-[#6938EF]" : "text-gray-900"}`}>
                  Contact Details

                  </span>
                </div>
                <span className={`transition-transform duration-300 ${openBasic ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>

              {openBasic && (
                isEditing ? (
                  <div className="grid gap-4 px-4 pb-4 grid-cols-1">

                  {(isEditing || formData.first_name) && (
                    <div ref={(el) => (fieldRefs.current["first_name"] = el)}>
                      <TextField
                        label="First Name"
                        name="first_name"
                        required
                        value={formData.first_name}
                        onChange={(e) => { handleChange(e); if (errors.first_name) setErrors((p) => ({ ...p, first_name: null })); }}
                        disabled={!isEditing}
                        startIcon="../input-logos/user.svg"
                        error={errors.first_name}
                      />
                    </div>
                  )}

                  {(isEditing || formData.last_name) && (
                    <div ref={(el) => (fieldRefs.current["last_name"] = el)}>
                      <TextField
                        label="Last Name"
                        name="last_name"
                        required
                        value={formData.last_name}
                        onChange={(e) => { handleChange(e); if (errors.last_name) setErrors((p) => ({ ...p, last_name: null })); }}
                        disabled={!isEditing}
                        startIcon="../input-logos/user.svg"
                        error={errors.last_name}
                      />
                    </div>
                  )}

                  {(isEditing || formData.personal_email_id) && (
                    <div ref={(el) => (fieldRefs.current["personal_email_id"] = el)}>
                      <TextField
                        label="Personal Email Id"
                        name="personal_email_id"
                        required
                        value={inlineEditingField === "personal_email_id" ? inlineDrafts.personal_email_id : formData.personal_email_id}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          if (inlineEditingField === "personal_email_id") {
                            updateInlineDraft("personal_email_id", nextValue);
                          } else {
                            handleChange(e);
                          }
                          if (errors.personal_email_id) setErrors((p) => ({ ...p, personal_email_id: null }));
                        }}
                        disabled={!isEditing || inlineEditingField !== "personal_email_id" || fieldSaving === "personal_email_id"}
                        startIcon="../input-logos/sms.svg"
                        rightIcon={getInlineActionButton("personal_email_id")}
                        error={errors.personal_email_id}
                      />
                    </div>
                  )}

                  {(isEditing || formData.work_email_id) && (
                    <div className="flex justify-end flex-col">
                      <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <label className="text-xs input-lable">Work Email Id<span className="text-red-500">*</span></label>
                        <label className="flex items-center gap-2 cursor-pointer self-start sm:self-auto">
                          <span className="text-sm text-gray-500">Same as above</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={sameAsAboveEmail}
                              onChange={handleToggleSameAsAboveEmail}
                              disabled={!isEditing}
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#6938EF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-[16px]" />
                          </label>
                        </label>
                      </div>
                      <div ref={(el) => (fieldRefs.current["work_email_id"] = el)}>
                        <TextField
                          name="work_email_id"
                          value={inlineEditingField === "work_email_id" ? inlineDrafts.work_email_id : formData.work_email_id}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            if (inlineEditingField === "work_email_id") {
                              updateInlineDraft("work_email_id", nextValue);
                            } else {
                              handleChange(e);
                            }
                            if (errors.work_email_id) setErrors((p) => ({ ...p, work_email_id: null }));
                          }}
                          placeholder="Enter Work Email"
                          disabled={!isEditing || sameAsAboveEmail || inlineEditingField !== "work_email_id" || fieldSaving === "work_email_id"}
                          startIcon="../input-logos/sms.svg"
                          rightIcon={sameAsAboveEmail ? null : getInlineActionButton("work_email_id")}
                          error={errors.work_email_id}
                        />
                      </div>
                    </div>
                  )}

                  {(isEditing || formData.personal_mobile) && (
                    <div ref={(el) => (fieldRefs.current["personal_mobile"] = el)}>
                      <PhoneField
                        label="Personal Mobile"
                        name="personal_mobile"
                        required
                        value={inlineEditingField === "personal_mobile" ? inlineDrafts.personal_mobile : formData.personal_mobile}
                        countryCode={formData.country_code_personal}
                        onChange={(e) => {
                          if (inlineEditingField === "personal_mobile") {
                            updateInlineDraft("personal_mobile", e.target.phoneNumber, {
                              valid: e.target.isValid,
                              numberType: e.target.numberType,
                            });
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              personal_mobile: e.target.phoneNumber,
                              country_code_personal: prev.country_code_personal || "+91",
                              secondary_mobile: sameAsPersonalMobile ? e.target.phoneNumber : prev.secondary_mobile,
                              country_code_home: sameAsPersonalMobile ? (prev.country_code_personal || "+91") : prev.country_code_home,
                              secondary_mobile_valid: sameAsPersonalMobile ? e.target.isValid : prev.secondary_mobile_valid,
                              secondary_mobile_type: sameAsPersonalMobile ? e.target.numberType : prev.secondary_mobile_type,
                              work_mobile: sameAsPersonalWorkMobile ? e.target.phoneNumber : prev.work_mobile,
                              work_mobile_country_code: sameAsPersonalWorkMobile ? (prev.country_code_personal || "+91") : prev.work_mobile_country_code,
                              work_mobile_valid: sameAsPersonalWorkMobile ? e.target.isValid : prev.work_mobile_valid,
                              work_mobile_type: sameAsPersonalWorkMobile ? e.target.numberType : prev.work_mobile_type,
                              personal_mobile_valid: e.target.isValid,
                              personal_mobile_type: e.target.numberType,
                            }));
                          }
                          if (errors.personal_mobile && e.target.isValid)
                            setErrors((p) => ({ ...p, personal_mobile: null }));
                        }}
                        disabled={!isEditing || inlineEditingField !== "personal_mobile" || fieldSaving === "personal_mobile"}
                        openCountryModal={() => { setCurrentPhoneField("personal"); setShowCountryModal(isEditing || inlineEditingField === "personal_mobile"); }}
                        error={errors.personal_mobile}
                        countryIso={selectedCountryIso.personal}
                        type="mobile"
                        rightIcon={getInlineActionButton("personal_mobile")}
                      />
                    </div>
                  )}

                  {(isEditing || formData.work_mobile) && (
                    <div className="flex flex-col justify-end">
                      <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <label className="text-xs input-lable">Work Mobile<span className="text-red-500">*</span></label>
                        <label className="flex items-center gap-2 cursor-pointer self-start sm:self-auto">
                          <span className="text-sm text-gray-500">Same as Mobile Number</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={sameAsPersonalWorkMobile}
                              onChange={handleToggleSameAsPersonalWorkMobile}
                              disabled={!isEditing}
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#6938EF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-[16px]" />
                          </label>
                        </label>
                      </div>
                      <div ref={(el) => (fieldRefs.current["work_mobile"] = el)}>
                        <PhoneField
                          name="work_mobile"
                          required
                          value={inlineEditingField === "work_mobile" ? inlineDrafts.work_mobile : formData.work_mobile}
                          countryCode={formData.work_mobile_country_code || "+91"}
                          onChange={(e) => {
                            if (inlineEditingField === "work_mobile") {
                              updateInlineDraft("work_mobile", e.target.phoneNumber, {
                                valid: e.target.isValid,
                                numberType: e.target.numberType,
                              });
                              return;
                            }
                            setFormData((prev) => ({
                              ...prev,
                              work_mobile: e.target.phoneNumber,
                              work_mobile_country_code: prev.work_mobile_country_code || "+91",
                              work_mobile_valid: e.target.isValid,
                              work_mobile_type: e.target.numberType,
                            }));
                          }}
                          placeholder="Enter Work Mobile"
                          disabled={!isEditing || inlineEditingField !== "work_mobile" || sameAsPersonalWorkMobile || fieldSaving === "work_mobile"}
                          openCountryModal={() => { setCurrentPhoneField("work_mobile"); setShowCountryModal(isEditing || inlineEditingField === "work_mobile"); }}
                          error={errors.work_mobile}
                          countryIso={selectedCountryIso.work_mobile}
                          type="mobile"
                          rightIcon={sameAsPersonalWorkMobile ? null : getInlineActionButton("work_mobile")}
                        />
                      </div>
                    </div>
                  )}

                  {(isEditing || formData.date_of_birth) && (
                    <div ref={(el) => (fieldRefs.current["date_of_birth"] = el)}>
                      <TextField
                        label="Date of Birth"
                        type="date"
                        required
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={(e) => { handleChange(e); if (errors.date_of_birth) setErrors((p) => ({ ...p, date_of_birth: null })); }}
                        disabled={!isEditing}
                        startIcon="../input-logos/calander.svg"
                        maxDate={getTodayISO()}
                        error={errors.date_of_birth}
                      />
                    </div>
                  )}

                  {(isEditing || formData.marital_status) && (
                    <div ref={(el) => (fieldRefs.current["marital_status"] = el)}>
                      <TextField
                        label="Marital Status"
                        name="marital_status"
                        required
                        value={formData.marital_status}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value !== "Married") {
                            setFormData((prev) => ({ ...prev, date_of_anniversary: "" }));
                            setErrors((prev) => ({ ...prev, date_of_anniversary: null }));
                          }
                          if (errors.marital_status) setErrors((p) => ({ ...p, marital_status: null }));
                        }}
                        disabled={!isEditing}
                        select
                        options={MARITAL_STATUS_OPTIONS}
                        error={errors.marital_status}
                      />
                    </div>
                  )}

                  {formData.marital_status === "Married" && (
                    <TextField
                      label="Anniversary Date"
                      type="date"
                      name="date_of_anniversary"
                      value={formData.date_of_anniversary}
                      onChange={(e) => {
                        handleChange(e);
                        if (errors.date_of_anniversary) setErrors((p) => ({ ...p, date_of_anniversary: null }));
                      }}
                      disabled={!isEditing}
                      required
                      startIcon="../input-logos/calander.svg"
                      maxDate={getTodayISO()}
                      error={errors.date_of_anniversary}
                    />
                  )}

                  {/* Birth City — plan 2+ only */}
                  {planType >= 2 && (isEditing || formData.birth_city_country) && (
                    <div ref={(el) => (fieldRefs.current["birth_city_country"] = el)}>
                      <TextField
                        label="Birth City and Country"
                        name="birth_city_country"
                        placeholder="e.g., Mumbai, India"
                        required
                        value={formData.birth_city_country}
                        onClick={openBirthCityCountryModal}
                        onFocus={(e) => {
                          if (!isEditing) return;
                          e.target.blur();
                          openBirthCityCountryModal();
                        }}
                        readOnly
                        disabled={!isEditing}
                      />
                    </div>
                  )}
                  {(isEditing || formData.basic_pincode_home) && (
                    <div ref={(el) => (fieldRefs.current["basic_pincode_home"] = el)}>
                      <TextField
                        label="Pincode - Home"
                        name="basic_pincode_home"
                        placeholder="Click to select pincode"
                        required
                        value={formData.basic_pincode_home}
                        onChange={handleChange}
                        onClick={() => {
                          if (isPincodeDisabledByAddress) {
                            const now = Date.now();
                            if (now - (lastPincodeToastRef.current || 0) > 1000) {
                              toast.dismiss();
                              toast("Pincode is derived from Address. To change it, edit the Address field and it will update automatically.", {
                                icon: "ℹ️",
                              });
                              lastPincodeToastRef.current = now;
                            }
                            return;
                          }
                          openLocationModalForPincode("personal-pincode");
                        }}
                        onFocus={(e) => {
                          if (!isEditing) return;
                          if (isPincodeDisabledByAddress) {
                            e.target.blur();
                            return;
                          }
                          e.target.blur();
                          openLocationModalForPincode("personal-pincode");
                        }}
                        readOnly
                        disabled={!isEditing || isPincodeDisabledByAddress}
                        title={isPincodeDisabledByAddress
                          ? "Pincode is derived from Address. Edit the Address field to update it."
                          : undefined}
                      />
                    </div>
                  )}

                  {(isEditing || formData.current_city_region_country) && (
                    <div ref={(el) => (fieldRefs.current["current_city_region_country"] = el)}>
                      <TextField
                        label="Current City, Region and Country"
                        name="current_city_region_country"
                        placeholder="Autofilled from pincode"
                        required
                        value={formData.current_city_region_country}
                        onChange={handleChange}
                        disabled={true}
                      />
                    </div>
                  )}

                  {(isEditing || formData.secondary_email) && (
                    <div ref={(el) => (fieldRefs.current["secondary_email"] = el)}>
                      <TextField
                        label="Secondary Email"
                        name="secondary_email"
                        placeholder="Enter Secondary Email"
                        value={formData.secondary_email}
                        onChange={handleChange}
                        disabled={!isEditing}
                        startIcon="../input-logos/sms.svg"
                        error={errors.secondary_email}
                      />
                    </div>
                  )}

                  {(isEditing || formData.secondary_mobile) && (
                    <div className="flex flex-col justify-end">
                      <div className="mb-1">
                        <label className="text-xs input-lable">Secondary Mobile</label>
                      </div>
                      <div ref={(el) => (fieldRefs.current["secondary_mobile"] = el)}>
                        <PhoneField
                          name="secondary_mobile"
                          value={inlineEditingField === "secondary_mobile" ? inlineDrafts.secondary_mobile : formData.secondary_mobile}
                          countryCode={formData.country_code_home}
                          onChange={(e) => {
                            if (inlineEditingField === "secondary_mobile") {
                              updateInlineDraft("secondary_mobile", e.target.phoneNumber, {
                                valid: e.target.isValid,
                                numberType: e.target.numberType,
                              });
                              return;
                            }
                            setFormData((prev) => ({
                              ...prev,
                              secondary_mobile: e.target.phoneNumber,
                              country_code_home: prev.country_code_home || "+91",
                              secondary_mobile_valid: e.target.isValid,
                              secondary_mobile_type: e.target.numberType,
                            }));
                          }}
                          placeholder="Enter Secondary mobile"
                          disabled={!isEditing || inlineEditingField !== "secondary_mobile" || fieldSaving === "secondary_mobile"}
                          openCountryModal={() => { setCurrentPhoneField("secondary"); setShowCountryModal(isEditing || inlineEditingField === "secondary_mobile"); }}
                          error={errors.secondary_mobile}
                          countryIso={selectedCountryIso.secondary}
                          type="mobile"
                          rightIcon={getInlineActionButton("secondary_mobile")}
                        />
                      </div>
                    </div>
                  )}

                  {(isEditing || formData.home_fixed) && (
                    <div ref={(el) => (fieldRefs.current["home_fixed"] = el)}>
                      <PhoneField
                        label="Home Fixed"
                        name="home_fixed"
                        value={formData.home_fixed}
                        countryCode={formData.country_code_home_fixed}
                        placeholder="Enter Home Fixed"
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            home_fixed: e.target.phoneNumber,
                            country_code_home_fixed: prev.country_code_home_fixed || "+91",
                            work_fixed: sameAsAboveFixed ? e.target.phoneNumber : prev.work_fixed,
                            country_code_work_fixed: sameAsAboveFixed ? (prev.country_code_home_fixed || "+91") : prev.country_code_work_fixed,
                            work_fixed_valid: sameAsAboveFixed ? e.target.isValid : prev.work_fixed_valid,
                            work_fixed_type: sameAsAboveFixed ? e.target.numberType : prev.work_fixed_type,
                            home_fixed_valid: e.target.isValid,
                            home_fixed_type: e.target.numberType,
                          }))
                        }
                        openCountryModal={() => { setCurrentPhoneField("home_fixed"); setShowCountryModal(isEditing); }}
                        type="fixed"
                        disabled={!isEditing}
                        error={errors.home_fixed}
                      />
                    </div>
                  )}

                  {(isEditing || formData.work_fixed) && (
                    <div className="flex flex-col justify-end">
                      <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <label className="text-xs input-lable">Work Fixed</label>
                        <label className="flex items-center gap-2 cursor-pointer self-start sm:self-auto">
                          <span className="text-sm text-gray-500">Same as above</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={sameAsAboveFixed}
                              onChange={handleToggleSameAsAboveFixed}
                              disabled={!isEditing}
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#6938EF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-[16px]" />
                          </label>
                        </label>
                      </div>
                      <div ref={(el) => (fieldRefs.current["work_fixed"] = el)}>
                        <PhoneField
                          name="work_fixed"
                          value={formData.work_fixed}
                          countryCode={formData.country_code_work_fixed}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              work_fixed: e.target.phoneNumber,
                              country_code_work_fixed: prev.country_code_work_fixed || "+91",
                              work_fixed_valid: e.target.isValid,
                              work_fixed_type: e.target.numberType,
                            }))
                          }
                          placeholder="Enter Work Fixed"
                          openCountryModal={() => { setCurrentPhoneField("work_fixed"); setShowCountryModal(isEditing); }}
                          type="fixed"
                          disabled={!isEditing || sameAsAboveFixed}
                          error={errors.work_fixed}
                        />
                      </div>
                    </div>
                  )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4">
                    {basicPreviewFields.map(renderPreviewFieldCard)}
                  </div>
                )
              )}
            </div>
          )}

          {/* Section 2: Address & Location */}
          {(isEditing || hasAddressData) && (
            <div className="mb-4 border border-gray-100 rounded-xl bg-white">
              <button
                type="button"
                onClick={() => setOpenAddress((prev) => !prev)}
                className={`sticky top-0 z-30 w-full flex items-center justify-between px-5 py-4 transition-all bg-white ${
                  openAddress
                    ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                    : "bg-gradient-to-r from-gray-50 to-white"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${openAddress ? "bg-[#6938EF]/10" : "bg-gray-200/50"}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 8 13 8 13s8-7.75 8-13c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" fill="#6938EF" />
                    </svg>
                  </div>
                  <span className={`font-semibold transition-colors ${openAddress ? "text-[#6938EF]" : "text-gray-900"}`}>
                  Home Address
                  </span>
                </div>
                <span className={`transition-transform duration-300 ${openAddress ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>

              {openAddress && (
                isEditing ? (
                  <div className="grid gap-4 px-4 pb-4 grid-cols-1">

                  {/* Flat Number — plan 2+ only */}
                  {planType >= 2 && (isEditing || formData.flat_number) && (
                    <div ref={(el) => (fieldRefs.current["flat_number"] = el)}>
                      <TextField
                        label="Flat Number / House Number"
                        name="flat_number"
                        placeholder="e.g., A-101"
                        required
                        value={formData.flat_number}
                        onChange={handleChange}
                        disabled={!isEditing}
                        error={errors.flat_number}
                      />
                    </div>
                  )}

                  {/* Building Name — plan 2+ only */}
                  {planType >= 2 && (isEditing || formData.building_name) && (
                    <div ref={(el) => (fieldRefs.current["building_name"] = el)}>
                      <TextField
                        label="Building Name / House Name"
                        name="building_name"
                        placeholder="e.g., Sunset Apartments"
                        required
                        value={formData.building_name}
                        onChange={handleChange}
                        disabled={!isEditing}
                        error={errors.building_name}
                      />
                    </div>
                  )}

                  {(isEditing || formData.address_google_map) && (
                    <div ref={(el) => (fieldRefs.current["address_google_map"] = el)}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs input-lable">Address</label>
                        {!isEditing && String(formData.address_google_map || "").trim() && (
                          <button
                            type="button"
                            onClick={handleCopyAddress}
                            className="text-xs font-medium text-[#6938EF] hover:text-[#5a2fd6]"
                          >
                            {isAddressCopied ? "Copied!" : "Copy"}
                          </button>
                        )}
                      </div>
                      <textarea
                        ref={addressTextareaRef}
                        name="address_google_map"
                        placeholder="Autofilled from Home Pincode"
                        required
                        value={formData.address_google_map}
                        onClick={() => {
                          if (!isEditing) return;
                          openGoogleMapFromAddressField();
                        }}
                        onFocus={(e) => {
                          if (!isEditing) return;
                          e.target.blur();
                          openGoogleMapFromAddressField();
                        }}
                        readOnly
                        rows={1}
                        className={`w-full border rounded-xl px-4 py-3 text-gray-700 resize-none overflow-hidden min-h-[48px] focus:outline-none ${errors.address_google_map ? "border-red-500" : "border-[#E6E2FF]"}`}
                      />
                      {errors.address_google_map && (
                        <p className="text-xs text-red-500 mt-1">{errors.address_google_map}</p>
                      )}
                    </div>
                  )}

                  </div>
                ) : (
                  <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4">
                    {addressPreviewFields.map(renderPreviewFieldCard)}
                  </div>
                )
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
